import type { Competitor } from '../types';

// Researched 2026-11-03. Every claim links to the page it came from.
// To correct something: change it here, update sourceUrl and lastReviewed.

export const polar: Competitor = {
  "id": "polar",
  "name": "Polar Analytics",
  "group": "ecomm",
  "website": "https://www.polaranalytics.com",
  "owner": "Polar Analytics (independent; no acquisition found)",
  "summary": "Ecommerce BI platform for Shopify and DTC brands that lands sales, ad and customer data in a dedicated Snowflake database per customer, with a prebuilt ecommerce semantic layer, dashboards, a first-party pixel and AI agents on top.",
  "bestFor": [
    "Shopify-led brands that want a managed Snowflake warehouse and prebuilt ecommerce metrics without hiring a data team",
    "Brands that want analysts to have SQL access later while marketers use no-code dashboards",
    "Teams that want AI/MCP access to consistent metrics from tools like Claude or ChatGPT"
  ],
  "notFor": [
    "Non-ecommerce businesses (vendor says it is not built for other verticals)",
    "Very small brands that only need a basic profit dashboard (vendor says feature depth may exceed their needs; entry price is several hundred dollars a month)",
    "Brands that need full control over transformation logic and modeling in their own dbt project"
  ],
  "strengths": [
    "Dedicated Snowflake database per customer is included, so the brand gets a warehouse without building or running ELT pipelines",
    "45+ native one-click connectors including Shopify, Amazon Seller Central, Amazon Vendor Central, TikTok Shop, Recharge and the main ad platforms",
    "400+ prebuilt ecommerce metrics (contribution margin, LTV, CAC, ROAS) defined once and shared across dashboards, AI and MCP",
    "Vendor claims setup in 48-72 hours versus months for a DIY warehouse build",
    "First-party pixel with multi-touch attribution and ad-platform conversion API signals included, which a reporting warehouse does not provide on its own",
    "Unlimited users and history on all plans, plus a dedicated success manager and Slack channel",
    "Managed geo-based incrementality testing run by a Polar data scientist (add-on)",
    "Missing sources can be added by its AI Data Engineer building a connector from the tool's API, or pushed into Snowflake via API/CSV in the meantime"
  ],
  "limitations": [
    "NetSuite is not a native connector; it is a custom connection built on request, and Faire detail beyond Shopify-routed orders also needs a custom connector",
    "SQL access to the Snowflake database is a paid add-on rather than included in the base plan",
    "Pricing is not published on the vendor site; it scales with annual online GMV and requires a demo for a quote",
    "Vendor itself says it is Shopify-focused and not built for non-ecommerce use",
    "Metric logic lives in Polar's semantic layer; custom metrics are formula-based in the app rather than a customer-owned dbt project (inferred from vendor docs, not stated as a limitation by Polar)",
    "No marketing mix modeling product found on vendor pages; incrementality is test-based only"
  ],
  "pricing": {
    "model": "revenue-based (annual online GMV)",
    "publicStartingPrice": "Core plan from $750/month (Shopify App Store listing)",
    "freeTier": "no free plan found; Polar MCP has a 7-day trial; third-party reports a 14-day trial (unverified on vendor site)",
    "notes": "Vendor pricing page shows Core Plan (bundles Business Intelligence, AI agents and data activation, said to save 20% vs buying separately) and Custom Plan (pick modules: Business Intelligence, Incrementality Testing, Headless MCP, Klaviyo Audiences, Advertising Signals), but no dollar figures. Shopify listing: Core from $750/month, priced on online GMV, discounts for annual terms. All plans include a dedicated Snowflake database, ecommerce semantic layer, first-party pixel, unlimited users and history. Add-ons include SQL access, intraday refresh and custom connectors. An older help-center grid (likely legacy Analyze / Analyze & Enrich / Activate plans) ran from $300/month under $1.5M GMV to $2,900/month at $15M-$20M, custom above $20M, with up to 17% off annually. Third-party review (June 2026) cites $720/month under $5M GMV for Core and incrementality at about $3,200/month.",
    "sourceUrl": "https://apps.shopify.com/polar-analytics",
    "checkedOn": "2026-11-03"
  },
  "features": {
    "shopify": {
      "value": "yes",
      "note": "Native sales channel connector",
      "sourceUrl": "https://intercom.help/polar-app/en/articles/5472989-available-connectors"
    },
    "amazon": {
      "value": "yes",
      "note": "Amazon Seller Central native; Amazon Vendor Central also listed",
      "sourceUrl": "https://intercom.help/polar-app/en/articles/5472989-available-connectors"
    },
    "tiktok_shop": {
      "value": "yes",
      "note": "Listed as one-click TikTok / TikTok Shop on connectors directory (not in older help-center list)",
      "sourceUrl": "https://www.polaranalytics.com/connectors?2c6784b1_page=1"
    },
    "wholesale_retail_or_erp": {
      "value": "partial",
      "note": "Brightpearl, Lightspeed (Vend), Linnworks, Ankorstore, Amazon Vendor Central listed; NetSuite and deep Faire data are custom builds",
      "sourceUrl": "https://polaranalytics.com/integrations/netsuite",
      "badge": "NetSuite is custom"
    },
    "ad_platforms": {
      "value": "yes",
      "note": "Facebook/Meta, Google, TikTok Ads and others",
      "sourceUrl": "https://intercom.help/polar-app/en/articles/5472989-available-connectors"
    },
    "first_party_pixel_attribution": {
      "value": "yes",
      "note": "Polar Pixel and multi-touch attribution included in all plans",
      "sourceUrl": "https://www.polaranalytics.com/pricing"
    },
    "mmm_incrementality": {
      "value": "partial",
      "note": "Geo incrementality testing as a paid add-on run by Polar data scientists; no MMM found",
      "sourceUrl": "https://www.polaranalytics.com/llms.txt",
      "badge": "Incrementality add-on"
    },
    "profit_cogs": {
      "value": "yes",
      "note": "Contribution margin among prebuilt metrics",
      "sourceUrl": "https://www.polaranalytics.com/llms.txt"
    },
    "ltv_cohorts": {
      "value": "yes",
      "note": "LTV among prebuilt metrics",
      "sourceUrl": "https://www.polaranalytics.com/llms.txt"
    },
    "subscription_analytics": {
      "value": "partial",
      "note": "Recharge connector available; depth of subscription reporting not verified",
      "sourceUrl": "https://intercom.help/polar-app/en/articles/5472989-available-connectors"
    },
    "warehouse_access": {
      "value": "yes",
      "note": "Dedicated Snowflake database per customer; SQL access is an add-on",
      "sourceUrl": "https://www.polaranalytics.com/alternatives/bigquery"
    },
    "custom_metrics": {
      "value": "yes",
      "note": "Formula-based custom metrics and conditional custom dimensions",
      "sourceUrl": "https://www.polaranalytics.com/llms.txt"
    },
    "custom_sql": {
      "value": "partial",
      "note": "SQL access sold as a Business Intelligence add-on",
      "sourceUrl": "https://www.polaranalytics.com/pricing",
      "badge": "Paid add-on"
    },
    "ai_assistant": {
      "value": "yes",
      "note": "Ask Polar, AI agents and a hosted MCP server",
      "sourceUrl": "https://www.polaranalytics.com/llms.txt"
    },
    "mobile_app": {
      "value": "unknown",
      "note": "No mobile app mentioned on vendor pages checked",
      "sourceUrl": "https://www.polaranalytics.com/llms.txt"
    }
  },
  "recentChanges": [
    {
      "date": "2026-01",
      "change": "Released Ask Polar 2.0, which answers questions, builds dashboards and cites sources",
      "sourceUrl": "https://www.polaranalytics.com/llms.txt"
    },
    {
      "date": "2026",
      "change": "Moved from published GMV-band plans (Analyze / Enrich / Activate) to a Core Plan plus modular Custom Plan with quote-based pricing; exact date not published",
      "sourceUrl": "https://www.polaranalytics.com/pricing"
    },
    {
      "date": "2026",
      "change": "Launched hosted Polar MCP / Headless MCP for Claude, ChatGPT and Copilot, an AI Data Engineer that builds custom connectors, and an agent catalog; exact dates not published",
      "sourceUrl": "https://www.polaranalytics.com/llms.txt"
    }
  ],
  "sources": [
    {
      "title": "Polar Analytics pricing",
      "url": "https://www.polaranalytics.com/pricing"
    },
    {
      "title": "Polar on Shopify App Store",
      "url": "https://apps.shopify.com/polar-analytics"
    },
    {
      "title": "Polar help: Pricing plans (older GMV grid)",
      "url": "https://intercom.help/polar-app/en/articles/6531316-pricing-plans"
    },
    {
      "title": "Polar help: Available connectors",
      "url": "https://intercom.help/polar-app/en/articles/5472989-available-connectors"
    },
    {
      "title": "Polar connectors directory",
      "url": "https://www.polaranalytics.com/connectors?2c6784b1_page=1"
    },
    {
      "title": "Polar NetSuite integration page",
      "url": "https://polaranalytics.com/integrations/netsuite"
    },
    {
      "title": "Polar Faire integration page",
      "url": "https://www.polaranalytics.com/integrations/faire"
    },
    {
      "title": "Polar vs BigQuery (vendor comparison)",
      "url": "https://www.polaranalytics.com/alternatives/bigquery"
    },
    {
      "title": "Polar llms.txt product summary",
      "url": "https://www.polaranalytics.com/llms.txt"
    },
    {
      "title": "Eightx: How much does Polar Analytics cost (third-party, June 2026)",
      "url": "https://eightx.co/blog/compare/how-much-does-polar-analytics-cost"
    }
  ],
  "lastReviewed": "2026-11-03"
};
